import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    AddEditModal,
    PhonePicker,
    ReactSelect,
    SpinnerLoader,
    TextareaInputWithCounter,
    TextInput,
} from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { Button, Col, Container, Form as BootstrapForm, Row } from "react-bootstrap";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { ErrorMessage, Field, FieldArray, Form, Formik, FormikHelpers, getIn } from "formik";
import CustomStylesForSaleOperations from "@services/ui-components/src/ReactSelect/CustomStylesForSaleOperations";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../../services/ErrorService";
import {
    useFindClientByPhoneOrEmailLazyQuery,
    useGetEmployeesByBranchQuery,
    useGetInfoCompanySaleQuery,
    useSaleOperationForBookingQuery,
    useSaleProductsMutation,
    useUpdateSaleProductsMutation,
} from "@me-team/host/main/salesOperation/graphql/salesOperation.hooks";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import {
    ClientProxy,
    CompanyAccess,
    PaymentType,
    PermissionScope,
    StorageOperationInput,
    StorageProductRelationActionType,
    StorageProductRelationHistoryProxy,
    UpdateSaleProductsContainerInput,
} from "@me-team/host/main/graphql/types";
import { PhoneInputRefType } from "react-international-phone";
import { useParams } from "react-router-dom";
import { currencySingVar, currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import ProductSaleListTable from "./ProductSaleListTable/ProductSaleListTable";
import { BookingDataForSale, ClientInput, PartialEmployeeForSale, SellProductForm } from "../types";
import { DeepPartial, Option, OptionString } from "../../../../interfaces/interfaces";
import DatePickerWithTime from "./DatePickerWithTime/DatePickerWithTime";
import {
    calculateTotalAmount,
    createDebouncedClientSearch,
    getInitialClient,
    getInitialEmployeeId,
    mapPaymentType,
} from "./utils/utils";
import { saleProductsValidationSchema } from "./utils/validationSchema";
import { DateTime } from "luxon";
import { normalizePhone } from "@me-team/host/src/utils/utils";
import { ChosenProductType } from "../../modals/modalsUtils";
import { useGetWarehousesOperationsQuery } from "@me-team/host/main/warehouses/graphql/warehouses.hooks";
import { getUpdatedFormValues } from "./utils/getUpdatedFormValues";
import {
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface SellProductsProps {
    action: "create" | "update";
    bookingUuid?: string;
    showModal: boolean;
    handleCloseModal: () => void;
    toggleShowToast: (text: string) => void;
    chosenProduct?: ChosenProductType;
    bookingDataForSale?: BookingDataForSale;
    refetch?: ReturnType<typeof useGetWarehousesOperationsQuery>["refetch"];
    bookingRefetch?: () => void;
    dialogClassName?: string;
    centeredModal?: boolean;
    dataTour?: string;
    canEditOperation?: boolean;
}

const SaleProducts: React.FC<SellProductsProps> = ({
    action,
    bookingUuid,
    showModal,
    handleCloseModal,
    toggleShowToast,
    chosenProduct,
    bookingDataForSale,
    refetch,
    bookingRefetch,
    dialogClassName,
    centeredModal,
    dataTour,
    canEditOperation,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const currencySing = useReactiveVar(currencySingVar);
    const isUpdateAction = action === "update";
    const canCreateSaleOperationAll = useRoleAccess(CompanyAccess.WarehouseOperationSaleCreate, {
        scope: PermissionScope.All,
    });
    const canCreateSaleOperationSelf = useRoleAccess(CompanyAccess.WarehouseOperationSaleCreate, {
        scope: PermissionScope.Self,
    });
    const canCreateSaleOperation = canCreateSaleOperationAll || canCreateSaleOperationSelf;
    const wasCanEditOperationProvided = typeof canEditOperation === "boolean";
    const phonePickerRef = useRef<PhoneInputRefType>(null);
    const tutorialActive = isWarehouseTutorialActive();
    const contentRef = useRef<HTMLDivElement>(null);

    const handleCloseModalWithTutorialPrev = useCallback(() => {
        if (tutorialActive) {
            moveStepInLocalStorage(StepDirection.PREV);
        }
        handleCloseModal();
    }, [tutorialActive, handleCloseModal]);

    useEffect(() => {
        if (contentRef.current && dataTour) {
            contentRef.current.setAttribute("data-tour", dataTour);
        }
    }, [dataTour, showModal]);

    const { data: timeZoneCompanyData, loading: timeZoneLoading } = useGetInfoCompanySaleQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        onError: (error: Error) => {
            console.error(error);
        },
    });
    const timeZoneName = timeZoneCompanyData?.user?.company?.timeZone?.name;
    const phoneCode = timeZoneCompanyData?.user?.company?.country?.isoCode.toLowerCase();
    const currentDateTime = DateTime.now().setZone(timeZoneName).toFormat("yyyy-MM-dd HH:mm:ss");
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [isClientFound, setIsClientFound] = useState(false);
    const [, setIsClientSearching] = useState(false);
    const [lastSearchField, setLastSearchField] = useState<"phone" | "email" | null>(null);
    const [totalAmount, setTotalAmount] = useState<string | null>(null);
    const [clientFound, setClientFound] = useState<Partial<ClientProxy>>(null);

    const [saleProducts] = useSaleProductsMutation();
    const [updateSaleProducts] = useUpdateSaleProductsMutation();

    const { data: employeesData, loading: employeesLoading } = useGetEmployeesByBranchQuery({
        variables: {
            companyId: +currentCompanyId,
            branchId: +branchId,
            onlyActive: true,
            itemsPerPage: 10000,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: saleOperationForBookingData, loading: saleOperationForBookingLoading } =
        useSaleOperationForBookingQuery({
            variables: {
                companyId: Number(currentCompanyId),
                branchId: Number(branchId),
                bookingUuid,
            },
            skip: !bookingUuid || !currentCompanyId || action === "create",
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });

    const saleOperationForBooking: DeepPartial<StorageProductRelationHistoryProxy> =
        saleOperationForBookingData?.user?.company?.branchesPaginated?.branches?.[0]
            .storageProductRelationHistoryPaginated?.entities?.[0] ?? null;

    const [findClientByPhoneOrEmail, { loading: clientLoading }] = useFindClientByPhoneOrEmailLazyQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const employees: PartialEmployeeForSale[] = employeesData?.user?.company?.employees?.employees;
    const currentEmployeeId: number = employeesData?.user?.employee?.id;
    const normalizedCurrentBranchEmployeeId = currentBranchEmployeeId
        ? Number(currentBranchEmployeeId)
        : currentEmployeeId;
    const shouldRestrictSaleCreateToSelf = canCreateSaleOperationSelf && !canCreateSaleOperationAll;
    const canCreateSaleForEmployee = (employeeId?: number | string | null): boolean => {
        if (action !== "create") return true;
        if (!canCreateSaleOperation) return false;
        if (!shouldRestrictSaleCreateToSelf) return true;

        return Boolean(
            employeeId &&
                normalizedCurrentBranchEmployeeId &&
                Number(employeeId) === Number(normalizedCurrentBranchEmployeeId)
        );
    };
    const canModifySaleOperation =
        action === "create"
            ? canCreateSaleOperation && (!shouldRestrictSaleCreateToSelf || Boolean(normalizedCurrentBranchEmployeeId))
            : wasCanEditOperationProvided
              ? Boolean(canEditOperation)
              : true;
    const isSaleOperationReadOnly = !canModifySaleOperation;
    const disableEditing = isSaleOperationReadOnly;

    const debouncedSearchClient = useRef(
        createDebouncedClientSearch(
            async (search: string, originalInput: { phone?: string; email?: string }) => {
                const trimmedSearch = search?.trim();
                const normalizedSearch = trimmedSearch?.includes("@")
                    ? trimmedSearch
                    : normalizePhone(trimmedSearch || null);
                const { data } =
                    normalizedSearch &&
                    (await findClientByPhoneOrEmail({
                        variables: {
                            companyId: +currentCompanyId,
                            search: normalizedSearch,
                        },
                    }));

                const foundClient = data?.user?.company?.clients?.clients?.[0];
                if (!foundClient) return null;

                let isMatch = false;
                if (originalInput.email) {
                    isMatch = foundClient.email?.toLowerCase() === originalInput.email.toLowerCase();
                } else if (originalInput.phone) {
                    isMatch = normalizePhone(foundClient.phone || "") === normalizePhone(originalInput.phone);
                }
                if (isMatch) {
                    setClientFound(foundClient);
                    return {
                        name: foundClient.name || null,
                        phone: foundClient.phone || null,
                        email: foundClient.email || null,
                    };
                }

                return null;
            },
            (error) => setIsErrorModalOpen(ErrorService.errorHandling(error)),
            (loading) => setIsClientSearching(loading)
        )
    ).current;

    const handlePhoneChange = (phone: string, setFieldValue: FormikHelpers<SellProductForm>["setFieldValue"]) => {
        if (isClientFound && lastSearchField === "phone") {
            setIsClientFound(false);
            setLastSearchField(null);
            setFieldValue("client.email", "");
            setFieldValue("client.name", "");
        }
        setFieldValue("client.phone", phone);
    };

    const handleEmailChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        setFieldValue: FormikHelpers<SellProductForm>["setFieldValue"]
    ) => {
        const email = e.target.value;
        if (isClientFound && lastSearchField === "email") {
            setIsClientFound(false);
            setLastSearchField(null);
            setFieldValue("client.phone", "");
            setFieldValue("client.name", "");
        }
        setFieldValue("client.email", email);
    };

    const handlePhoneBlur = (
        e: React.FocusEvent<HTMLInputElement>,
        setFieldValue: FormikHelpers<SellProductForm>["setFieldValue"]
    ) => {
        setClientFound(null);
        const normalizePhone = (phone: string) => phone.replace(/[\s()]/g, "");
        const rawPhone = e.target.value.trim();
        const normalizedPhone = normalizePhone(rawPhone);

        if (normalizedPhone.length >= 8) {
            setLastSearchField("phone");
            debouncedSearchClient(normalizedPhone, { phone: rawPhone }, (client: ClientInput) => {
                if (client) {
                    setIsClientFound(true);
                    setFieldValue("client", client);
                } else {
                    setIsClientFound(false);
                    setLastSearchField(null);
                }
            });
        }
    };

    const handleEmailBlur = async (
        e: React.ChangeEvent<HTMLInputElement>,
        setFieldValue: FormikHelpers<SellProductForm>["setFieldValue"],
        setFieldTouched: FormikHelpers<SellProductForm>["setFieldTouched"]
    ) => {
        setClientFound(null);
        const email = e.target.value;
        const normalizedEmail = email.trim().toLowerCase();
        await setFieldValue("client.email", normalizedEmail);
        setFieldTouched("client.email", true, true);
        if (normalizedEmail.includes("@")) {
            setLastSearchField("email");
            debouncedSearchClient(normalizedEmail, { email: normalizedEmail }, (client: ClientInput) => {
                if (client) {
                    setIsClientFound(true);
                    setFieldValue("client", client);
                } else {
                    setIsClientFound(false);
                    setLastSearchField(null);
                }
            });
        }
    };

    const employeesOptions: Option[] =
        employees?.map((employee) => {
            return {
                value: employee.id,
                label: `${t(employee.name)} ${t(employee.surname)}`,
            };
        }) || [];
    const availableEmployeesOptions = shouldRestrictSaleCreateToSelf
        ? employeesOptions.filter((employee) => Number(employee.value) === Number(normalizedCurrentBranchEmployeeId))
        : employeesOptions;

    const paymentTypeOptions: OptionString[] = [
        { value: "card", label: t("Card") },
        { value: "cash", label: t("Cash") },
    ];

    const initialValues: SellProductForm = {
        dateTime: chosenProduct?.date || currentDateTime,
        employee:
            action === "create" && shouldRestrictSaleCreateToSelf
                ? normalizedCurrentBranchEmployeeId
                : getInitialEmployeeId(action, bookingUuid, bookingDataForSale, chosenProduct, currentEmployeeId),
        client: getInitialClient(action, bookingUuid, bookingDataForSale, chosenProduct),

        productsInput:
            action === "update" && chosenProduct?.relatedSaleHistories
                ? chosenProduct.relatedSaleHistories.map((history) => ({
                      product: history.product?.id,
                      warehouse: history.storage?.id,
                      quantity: history.operationQuantity ?? 1,
                      unit: history?.operationUnit?.id,
                      price: Number(history.price),
                      amount: Number(history.amount),
                      discount: history.discount ?? null,
                      isAmountManuallyEdited: false,
                      storageProductRelationId: null,
                      storageProductRelationHistoryId: history.id,
                  }))
                : [
                      {
                          product: null,
                          warehouse: null,
                          quantity: 1,
                          unit: null,
                          price: null,
                          amount: null,
                          discount: null,
                          isAmountManuallyEdited: false,
                          storageProductRelationId: null,
                      },
                  ],
        isPaid: chosenProduct?.isPaid || false,
        paymentType: chosenProduct?.paymentType || null,
        comment: chosenProduct?.comment || "",
        operationNumber: chosenProduct?.operationNumber,
    };

    const handleCreateSubmit = async (values: SellProductForm) => {
        if (!canModifySaleOperation || !canCreateSaleForEmployee(values.employee)) return;

        const input: StorageOperationInput[] = values.productsInput.map((item) => ({
            storageProductRelationId: item.storageProductRelationId,
            updateStorageProductRelationInput: {
                quantity: item.quantity,
                unit: item.unit,
                action: StorageProductRelationActionType.Sale,
                comment: values.comment || null,
                saleProduct: {
                    product: item.product,
                    price: Number(item.price),
                    quantity: item.quantity,
                    unit: item.unit,
                    discount: item.discount || null,
                    amount: Number(item.amount) || 0.01,
                },
                client: values.client?.name
                    ? {
                          name: values.client?.name,
                          phone: values.client?.phone,
                          email: values.client?.email,
                      }
                    : null,
                employee: values.employee,
                dateTime: values.dateTime,
                isPaid: values.isPaid,
                paymentType: values.isPaid ? (values.paymentType.toUpperCase() as PaymentType) : null,
            },
        }));
        await saleProducts({
            variables: {
                branchId: Number(branchId),
                input,
                bookingUuid: bookingUuid ?? null,
                employeeId: Number(normalizedCurrentBranchEmployeeId),
            },
            context: {
                errorType: "local",
            },
            onCompleted: async () => {
                tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                tutorialActive && window.dispatchEvent(new Event("warehouseStepChanged"));
                handleCloseModal();
                toggleShowToast(t("Created"));
                bookingRefetch && (await bookingRefetch());
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });
    };

    const handleUpdateSubmit = async (values: SellProductForm) => {
        if (!canModifySaleOperation) return;

        const input: UpdateSaleProductsContainerInput[] = [
            {
                branch: Number(branchId),
                operationNumber: values?.operationNumber,
                bookingUuid: bookingUuid ? bookingUuid : null,
                employee: values?.employee,
                isPaid: values?.isPaid,
                paymentType: mapPaymentType(values?.paymentType),
                client: values.client?.name
                    ? {
                          name: values.client?.name,
                          phone: values.client?.phone,
                          email: values.client?.email,
                      }
                    : null,
                dateTime: values?.dateTime,
                comment: values?.comment || "",
                products: values.productsInput.map((saleProduct) => ({
                    product: saleProduct.product,
                    price: Number(saleProduct.price),
                    quantity: saleProduct?.quantity,
                    unit: saleProduct.unit,
                    discount: Number(saleProduct.discount) || null,
                    amount: Number(saleProduct.amount) || 0.01,
                    storage: saleProduct?.warehouse,
                })),
            },
        ];

        await updateSaleProducts({
            variables: {
                branchId: Number(branchId),
                employeeId: Number(normalizedCurrentBranchEmployeeId),
                input,
            },
            onCompleted: async () => {
                tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                tutorialActive && window.dispatchEvent(new Event("warehouseStepChanged"));
                handleCloseModal();
                toggleShowToast(t("Save"));
                refetch && (await refetch());
                bookingRefetch && (await bookingRefetch());
            },
            onError: (error) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });
    };

    if (employeesLoading || timeZoneLoading || saleOperationForBookingLoading) return <Spinner heightContent />;

    return (
        <>
            <AddEditModal
                isModalOpened={showModal}
                setIsModalOpened={handleCloseModalWithTutorialPrev}
                title={t(isUpdateAction ? "Edit Product Sales" : "Product Sale")}
                size="xl"
                contentClassName="wrapper-sell-product-modal"
                modalBodyClass="pb-0 px-0"
                dialogClassName={dialogClassName ? dialogClassName : ""}
                centered={centeredModal}
                dataTour={dataTour}
            >
                <Formik
                    initialValues={initialValues}
                    validationSchema={saleProductsValidationSchema(t, timeZoneName, action === "update")}
                    onSubmit={isUpdateAction ? handleUpdateSubmit : handleCreateSubmit}
                >
                    {({ setFieldValue, errors, values, touched, isSubmitting, setFieldTouched, setValues }) => {
                        // eslint-disable-next-line
                        useEffect(() => {
                            setValues((prev) =>
                                getUpdatedFormValues(prev, {
                                    action,
                                    bookingUuid,
                                    currentDateTime,
                                    chosenProduct,
                                    saleOperationEntity: saleOperationForBooking,
                                })
                            );
                            // eslint-disable-next-line
                        }, [action, bookingUuid, chosenProduct, saleOperationForBooking]);
                        // eslint-disable-next-line
                        useEffect(() => {
                            if (values.productsInput) {
                                const total =
                                    calculateTotalAmount(values.productsInput) ||
                                    (values.productsInput[0].storageProductRelationId && values?.productsInput?.[0].unit
                                        ? 0.01
                                        : 0);
                                setTotalAmount(total.toFixed(2));
                            }
                        }, [values?.productsInput]);

                        return (
                            <Form id="form">
                                <Container className="pb-3 px-3">
                                    <Row className="m-0 mb-3 mb-lg-4 h-100">
                                        <Col sm={3} md={2} className="mb-2 mb-sm-0 text-start">
                                            <label
                                                htmlFor="sellProducts-dateTime-field"
                                                className="text-dark fs-7 fw-bold py-1 h-100"
                                            >
                                                <span className="required-label">
                                                    {t("Date and Time of Operation Creation")}
                                                </span>
                                            </label>
                                        </Col>
                                        <Col sm={9} md={10} className="text-start">
                                            <DatePickerWithTime
                                                id="sellProducts-dateTime-field"
                                                name="dateTime"
                                                value={values?.dateTime}
                                                placeholder="dd.mm.yyyy hh:mm"
                                                timeZone={timeZoneName}
                                                disabled={disableEditing}
                                            />
                                            <ErrorMessage name="dateTime">
                                                {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                            </ErrorMessage>
                                        </Col>
                                    </Row>
                                    <Row className="m-0 mb-4 h-100" style={{ minHeight: "49px" }}>
                                        <Col sm={3} md={2} className="mb-2 mb-md-0">
                                            <label
                                                htmlFor="saleProducts-employee-field"
                                                className="text-dark fs-7 fw-bold py-1 h-100 d-flex align-items-center"
                                            >
                                                <span className="required-label text-break text-start">
                                                    {t("Employee")}
                                                </span>
                                            </label>
                                        </Col>
                                        <Col sm={9} md={10} className="text-start">
                                            <ReactSelect
                                                id="saleProducts-employee-field"
                                                name="employee"
                                                placeholder={t("Select a employee")}
                                                options={availableEmployeesOptions}
                                                value={availableEmployeesOptions.find(
                                                    (option) => option.value === values.employee
                                                )}
                                                onChange={(selectedOption: Option) =>
                                                    setFieldValue("employee", selectedOption.value)
                                                }
                                                stylesSelect={CustomStylesForSaleOperations(!errors, !touched)}
                                                isSearchable={true}
                                                disabled={disableEditing}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <h6 className="fw-bold text-start px-6 mb-3">{t("Client information")}</h6>
                                        </Col>
                                    </Row>
                                    <Row className="px-6 mb-3 mb-lg-4">
                                        <Col lg={4} className="pe-lg-2 mb-3 mb-lg-0 d-sm-flex flex-lg-column">
                                            <Col
                                                xs
                                                sm={3}
                                                md={2}
                                                lg={12}
                                                className="mb-2 d-flex align-items-center text-start"
                                            >
                                                <label
                                                    htmlFor="saleProducts-client.phone-field"
                                                    className="text-dark fs-7 fw-bold"
                                                    onClick={() => phonePickerRef.current?.focus()}
                                                >
                                                    {t("phone")}
                                                </label>
                                            </Col>
                                            <Col xs sm={9} md={10} lg={12}>
                                                <PhonePicker
                                                    id="saleProducts-client.phone-field"
                                                    phoneRef={phonePickerRef}
                                                    name="client.phone"
                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                        handlePhoneBlur(e, setFieldValue)
                                                    }
                                                    onChange={(value) => handlePhoneChange(value, setFieldValue)}
                                                    disabled={
                                                        clientLoading ||
                                                        (isClientFound &&
                                                            !!clientFound?.phone &&
                                                            lastSearchField !== "phone") ||
                                                        disableEditing
                                                    }
                                                    value={values?.client?.phone || ""}
                                                    defaultCountry={phoneCode}
                                                />
                                                <ErrorMessage name="client.phone">
                                                    {(msg) => (
                                                        <div className="fs-7 m-0 p-1 text-danger text-start">{msg}</div>
                                                    )}
                                                </ErrorMessage>
                                            </Col>
                                        </Col>
                                        <Col lg={4} className="px-lg-2 mb-3 mb-lg-0 d-sm-flex flex-lg-column">
                                            <Col xs sm={3} md={2} lg={12} className="mb-2 d-flex align-items-center">
                                                <label
                                                    htmlFor="saleProducts-client.email-field"
                                                    className="text-dark fs-7 fw-bold"
                                                >
                                                    {t("E-mail")}
                                                </label>
                                            </Col>
                                            <Col xs sm={9} md={10} lg={12}>
                                                <Field
                                                    id="saleProducts-client.email-field"
                                                    name="client.email"
                                                    placeholder={t("Enter E-mail")}
                                                    className={`form-control fs-7  ${
                                                        getIn(touched, "client.email") && getIn(errors, "client.email")
                                                            ? "is-invalid"
                                                            : ""
                                                    }`}
                                                    onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleEmailBlur(e, setFieldValue, setFieldTouched)
                                                    }
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleEmailChange(e, setFieldValue)
                                                    }
                                                    disabled={
                                                        clientLoading ||
                                                        (isClientFound &&
                                                            !!clientFound?.email &&
                                                            lastSearchField !== "email") ||
                                                        disableEditing
                                                    }
                                                />
                                                <ErrorMessage name="client.email">
                                                    {(msg) => <div className="invalid-feedback text-start">{msg}</div>}
                                                </ErrorMessage>
                                            </Col>
                                        </Col>
                                        <Col lg={4} className="ps-lg-2 d-sm-flex flex-lg-column">
                                            <Col xs sm={3} md={2} lg={12} className="mb-2 d-flex align-items-center">
                                                <label
                                                    htmlFor="saleProducts-client.name-field"
                                                    className="text-dark fs-7 fw-bold"
                                                >
                                                    {t("Name")}
                                                </label>
                                            </Col>
                                            <TextInput
                                                id="saleProducts-client.name-field"
                                                name="client.name"
                                                placeholder="Enter Name"
                                                label={t("Name")}
                                                maxLength={50}
                                                isLabel={false}
                                                inputCol={10}
                                                inputColLg={12}
                                                disabled={clientLoading || isClientFound || disableEditing}
                                            />
                                        </Col>
                                    </Row>

                                    <FieldArray name="productsInput">
                                        {({ push, remove }) => (
                                            <>
                                                <Row className="mb-3">
                                                    <Col lg={6} className="d-flex align-items-center">
                                                        <h6 className="fw-bold text-start px-6 mb-3 mb-lg-0">
                                                            {t("List of products")}
                                                        </h6>
                                                    </Col>
                                                    <Col lg={6} className="text-end">
                                                        <Button
                                                            variant="primary"
                                                            className="py-1 me-6"
                                                            onClick={() => {
                                                                push({
                                                                    product: null,
                                                                    warehouse: null,
                                                                    quantity: 1,
                                                                    unit: null,
                                                                    price: null,
                                                                    amount: null,
                                                                    discount: null,
                                                                    storageProductRelationId: null,
                                                                });
                                                            }}
                                                            disabled={disableEditing}
                                                        >
                                                            <i className="bi bi-plus-lg me-1"></i>
                                                            {t("Add product")}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                                <Row className="px-6">
                                                    <ProductSaleListTable
                                                        remove={remove}
                                                        action={action}
                                                        disableEditing={disableEditing}
                                                        isSaleOperationReadOnly={isSaleOperationReadOnly}
                                                    />
                                                </Row>
                                            </>
                                        )}
                                    </FieldArray>
                                    <Row className="my-3">
                                        <Col className="text-start">
                                            <span className="ps-6 me-2">{t("Total amount")}:</span>
                                            <span className="fw-bold">
                                                {totalAmount} {currencySing}
                                            </span>
                                        </Col>
                                    </Row>
                                    <Row className="m-0 mb-3 mb-sm-4 h-100" style={{ minHeight: "49px" }}>
                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                            <label
                                                htmlFor="saleProducts-paymentType-field"
                                                className="text-dark fs-7 fw-bold py-1 h-100 d-flex flex-wrap align-items-center gap-2 text-start"
                                            >
                                                {t("Sale.Paid")}
                                                <BootstrapForm.Check
                                                    type="checkbox"
                                                    checked={values?.isPaid}
                                                    onChange={(e) => setFieldValue("isPaid", e.target.checked)}
                                                    id="sellProducts-paymentType-checkbox"
                                                    disabled={disableEditing}
                                                />
                                            </label>
                                        </Col>
                                        <Col sm={9} md={10} className="text-start">
                                            <div className="position-relative h-100">
                                                <ReactSelect
                                                    id="saleProducts-paymentType-field"
                                                    name="paymentType"
                                                    placeholder={t("Select a payment type")}
                                                    options={paymentTypeOptions}
                                                    value={paymentTypeOptions.find(
                                                        (option) => option.value === values.paymentType
                                                    )}
                                                    onChange={(selectedOption: Option) =>
                                                        setFieldValue("paymentType", selectedOption.value)
                                                    }
                                                    stylesSelect={CustomStylesForSaleOperations(
                                                        !!errors?.paymentType,
                                                        !!touched?.paymentType,
                                                        false,
                                                        false,
                                                        true
                                                    )}
                                                    isSearchable={true}
                                                    disabled={!values?.isPaid || disableEditing}
                                                />
                                                <small className="fs-8 text-grey position-absolute top-0 left-0 py-1 ps-6">
                                                    {t("Payment type")}
                                                </small>
                                                <ErrorMessage name="paymentType">
                                                    {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                </ErrorMessage>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="px-6">
                                        <TextareaInputWithCounter
                                            id="saleProducts-comment-field"
                                            name="comment"
                                            labelCol={2}
                                            inputCol={10}
                                            label={t("Comment")}
                                            maxLength={400}
                                            placeholder={t("Enter a Comment")}
                                            disabled={disableEditing}
                                        />
                                    </Row>
                                </Container>
                                <Container>
                                    <Row className="d-flex justify-content-end py-6">
                                        <Col xs={6} sm="auto" className="mb-sm-0 pe-1">
                                            <Button
                                                onClick={() => {
                                                    handleCloseModalWithTutorialPrev();
                                                }}
                                                variant="outline-primary"
                                                type="button"
                                                className="custom-btn fw-normal"
                                            >
                                                {t("Cancel")}
                                            </Button>
                                        </Col>
                                        <Col xs={6} sm="auto" className="mb-sm-0 ps-1">
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                className="custom-btn"
                                                disabled={isSubmitting || disableEditing}
                                            >
                                                {isSubmitting ? (
                                                    <SpinnerLoader variant="light" loadingText={t("Creating...")} />
                                                ) : (
                                                    t("Save")
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Container>
                            </Form>
                        );
                    }}
                </Formik>
            </AddEditModal>

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};
export default SaleProducts;
